import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Cookie, Settings } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useLocale } from '../../contexts/LocaleContext';
import { api } from '../../services/api';
import { loadAnalyticsIfConsented } from '../../utils/analyticsConsent';
import '../AllCss/CookieConsent.css';

const CookieConsent = () => {
  const location = useLocation();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { t } = useLocale();

  // Don't show cookie consent on superadmin routes (admin-only area, no customer cookies needed)
  const isSuperAdminRoute = location.pathname.startsWith('/superadmin');
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    cookieConsent: false,
    analyticsEnabled: false,
    marketingEmails: false,
    dataSharing: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    }

    // Load privacy settings if authenticated
    if (isAuthenticated) {
      loadPrivacySettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadPrivacySettings is stable, only run when isAuthenticated changes
  }, [isAuthenticated]);

  const loadPrivacySettings = async () => {
    try {
      const response = await api.get('/privacy/settings', {
              });
      if (response.data.success) {
        setSettings(response.data.data.settings || settings);
      }
    } catch (error) {
      console.error('Error loading privacy settings:', error);
    }
  };

  const handleAcceptAll = async () => {
    const newSettings = {
      cookieConsent: true,
      analyticsEnabled: true,
      marketingEmails: true,
      dataSharing: true,
    };

    await saveSettings(newSettings);
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    loadAnalyticsIfConsented();
  };

  const handleRejectAll = async () => {
    const newSettings = {
      cookieConsent: true,
      analyticsEnabled: false,
      marketingEmails: false,
      dataSharing: false,
    };

    await saveSettings(newSettings);
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    loadAnalyticsIfConsented();
  };

  const handleSaveSettings = async () => {
    await saveSettings(settings);
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
    loadAnalyticsIfConsented();
  };

  const saveSettings = async (newSettings) => {
    if (isAuthenticated) {
      try {
        setSaving(true);
        await api.put('/privacy/settings', newSettings, {
                  });
        setSettings(newSettings);
      } catch (error) {
        console.error('Error saving privacy settings:', error);
      } finally {
        setSaving(false);
      }
    } else {
      // For guests, just store in localStorage
      localStorage.setItem('privacySettings', JSON.stringify(newSettings));
      setSettings(newSettings);
    }
  };

  if (isSuperAdminRoute || !showBanner) return null;

  return (
    <>
      {/* Cookie Consent Banner */}
      <div className="cookie-consent-banner">
        <div className="cookie-consent-content">
          <div className="cookie-consent-icon">
            <Cookie size={24} />
          </div>
          <div className="cookie-consent-text">
            <h3>{t('cookie.weUseCookies')}</h3>
            <p>
              {t('cookie.cookieMessage')}
            </p>
          </div>
          <div className="cookie-consent-actions">
            <button
              className="cookie-btn-secondary"
              onClick={() => setShowSettings(true)}
            >
              <Settings size={16} />
              {t('cookie.customize')}
            </button>
            <button
              className="cookie-btn-reject"
              onClick={handleRejectAll}
            >
              {t('cookie.rejectAll')}
            </button>
            <button
              className="cookie-btn-accept"
              onClick={handleAcceptAll}
            >
              {t('cookie.acceptAll')}
            </button>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="cookie-settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="cookie-settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cookie-settings-header">
              <h2>{t('cookie.cookiePreferences')}</h2>
              <button
                className="cookie-settings-close"
                onClick={() => setShowSettings(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="cookie-settings-body">
              <p className="cookie-settings-description">
                {t('cookie.managePreferences')}
              </p>

              <div className="cookie-setting-item">
                <div className="cookie-setting-info">
                  <h4>{t('cookie.essentialCookies')}</h4>
                  <p>{t('cookie.essentialDesc')}</p>
                </div>
                <div className="cookie-setting-toggle">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                  />
                </div>
              </div>

              <div className="cookie-setting-item">
                <div className="cookie-setting-info">
                  <h4>Analytics Cookies</h4>
                  <p>Help us understand how visitors interact with our website.</p>
                </div>
                <div className="cookie-setting-toggle">
                  <input
                    type="checkbox"
                    checked={settings.analyticsEnabled}
                    onChange={(e) => setSettings({ ...settings, analyticsEnabled: e.target.checked })}
                  />
                </div>
              </div>

              <div className="cookie-setting-item">
                <div className="cookie-setting-info">
                  <h4>{t('cookie.marketingCookies')}</h4>
                  <p>{t('cookie.marketingDesc')}</p>
                </div>
                <div className="cookie-setting-toggle">
                  <input
                    type="checkbox"
                    checked={settings.marketingEmails}
                    onChange={(e) => setSettings({ ...settings, marketingEmails: e.target.checked })}
                  />
                </div>
              </div>

              <div className="cookie-setting-item">
                <div className="cookie-setting-info">
                  <h4>{t('cookie.dataSharing')}</h4>
                  <p>{t('cookie.dataSharingDesc')}</p>
                </div>
                <div className="cookie-setting-toggle">
                  <input
                    type="checkbox"
                    checked={settings.dataSharing}
                    onChange={(e) => setSettings({ ...settings, dataSharing: e.target.checked })}
                  />
                </div>
              </div>
            </div>
            <div className="cookie-settings-footer">
              <button
                className="cookie-btn-secondary"
                onClick={() => setShowSettings(false)}
              >
                {t('profile.cancel')}
              </button>
              <button
                className="cookie-btn-accept"
                onClick={handleSaveSettings}
                disabled={saving}
              >
                {saving ? t('cookie.saving') : t('cookie.savePreferences')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;

